/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment } from "react"
import { Col, InputNumber, Row, Switch, Tooltip } from "@duxton-web/ui"
import { MoreInfoOutlined } from "@duxton-web/icons"
import { Form } from "grab-ui"
import { BCRSInfo } from "types/models/menu"
import { BCRSConfig } from "store/duck/global.type"
import { getBcrsUnitCountLimits } from "constants/service_based_menu"
import { makeBcrsUnitCountRangeValidator } from "utils/form_helper"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useIsBcrsEnabled } from "pages/menu/detail/hooks/useIsBcrsEnabled"
import { drawerContentStyle } from "components/drawers/styles"

import styles from "./BCRSItemSection.modules.css"

interface BCRSItemSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  bcrsInfo?: BCRSInfo
  isAllowEdit?: boolean
  bcrsConfig?: BCRSConfig
}

export const BCRS_ELIGIBLE_FIELD = "bcrsEligible"
export const BCRS_UNIT_COUNT_FIELD = "bcrsUnitCount"

export const BCRSItemSection: FC<BCRSItemSectionProps> = ({
  form,
  bcrsInfo,
  isAllowEdit = true,
  bcrsConfig,
}) => {
  const textMap = useTranslationsMapTyped({
    sectionHeader: "menu.bcrs.item.sectionHeader",
    eligibleLabel: "menu.bcrs.item.eligibleLabel",
    eligibleTooltip: "menu.bcrs.item.eligibleTooltip",
    unitCountLabel: "menu.bcrs.item.unitCountLabel",
    unitCountTooltip: "menu.bcrs.item.unitCountTooltip",
    requiredError: "menu.errors.requiredField",
    unitCountRangeError: "menu.bcrs.item.unitCountRangeError",
  })

  const isFlagEnabled = useIsBcrsEnabled()

  if (!isFlagEnabled) {
    return null
  }

  const initialEligible = !!bcrsInfo?.eligible
  const { min, max } = getBcrsUnitCountLimits(bcrsConfig)
  // New items have no bcrsInfo yet — default the unit count to the configured
  // min (not a hardcoded 1) so a config with unitCountMin > 1 doesn't prefill
  // an out-of-range value that fails validation on save. Existing persisted
  // values are kept untouched.
  const initialUnitCount = bcrsInfo?.unitCount ?? min
  const rangeErrorMessage = textMap.unitCountRangeError
    .replace("<min>", String(min))
    .replace("<max>", String(max))
  const isFromGrabDB = bcrsInfo?.source === "grab"

  const eligibleFieldValue = form.getFieldValue(BCRS_ELIGIBLE_FIELD)
  const isEligible =
    eligibleFieldValue !== undefined ? eligibleFieldValue : initialEligible

  return (
    <Fragment>
      <div className="section-separator">
        <span>{textMap.sectionHeader}</span>
      </div>
      <div css={drawerContentStyle}>
        <Row className={styles.bcrsRow}>
          <Col span={24}>
            <Form.Item className={styles.switchRow}>
              {form.getFieldDecorator(BCRS_ELIGIBLE_FIELD, {
                initialValue: initialEligible,
                valuePropName: "checked",
              })(
                <Switch
                  data-testid="bcrs-eligible-switch"
                  disabled={!isAllowEdit || isFromGrabDB}
                />,
              )}
              <span className={styles.bcrsLabel}>{textMap.eligibleLabel}</span>
              <Tooltip overlay={textMap.eligibleTooltip}>
                <MoreInfoOutlined className={styles.bcrsInfoIcon} />
              </Tooltip>
            </Form.Item>
          </Col>
        </Row>
        {isEligible && (
          <Row className={styles.bcrsRow}>
            <Col span={24}>
              <Form.Item
                hasFeedback
                label={
                  <span>
                    {textMap.unitCountLabel}
                    <Tooltip overlay={textMap.unitCountTooltip}>
                      <MoreInfoOutlined className={styles.bcrsInfoIcon} />
                    </Tooltip>
                  </span>
                }
              >
                {form.getFieldDecorator(BCRS_UNIT_COUNT_FIELD, {
                  initialValue: initialUnitCount,
                  rules: [
                    {
                      required: true,
                      message: textMap.requiredError,
                    },
                    {
                      validator: makeBcrsUnitCountRangeValidator(
                        min,
                        max,
                        rangeErrorMessage,
                      ),
                    },
                  ],
                })(
                  <InputNumber
                    data-testid="bcrs-unit-count-input"
                    controls={false}
                    precision={0}
                    disabled={!isAllowEdit || isFromGrabDB}
                    style={{ width: "100%" }}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
        )}
      </div>
    </Fragment>
  )
}
